import React, { useState, useEffect } from "react";
import { Grid, Button, FormControl, Typography} from "@material-ui/core";
import Container from "../../components/common/Container";
import FormSelect from "../../components/common/FormSelect";
import FormTextInput from "../../components/common/FormTextInput";
import RadioButton from "../../components/common/RadioButton";
import HardwareSourceAndSensorGroupSelects from "./HardwareSourceAndSensorGroupSelects";
import { REGION_HARDWARE_SOURCE_MAP, REGION_DROPDOWN, ITEM_ID_TYPE_DROPDOWN, HARDWARE_SOURCE_TO_INCOMPLETE_CAPTURE_MAP } from "./constant";
import { inputValidation } from "src/utils/inputValidation";
import { RequestInputData } from "src/model/RequestInputData";
import { useStyles } from "src/styles/SearchFormStyles";


interface SearchFormProps {
    initialInputData?: RequestInputData;
    onSubmit: (value: RequestInputData ) => void
}

/**
 * The Component is used to provide the user a form, allows users to select/input Item Identifier Type, 
 * Item Identifier, Hardware Source, etc. and search Captures. It will check if users input a valid Item Identifier and provide all required data.
 */
 const SearchForm: React.FunctionComponent<SearchFormProps> = ({ initialInputData, onSubmit }) => {
    const styles = useStyles();
    const [region, setRegion] = useState('');
    const [hardwareSource, setHardwareSource] = useState('');
    const [sensor, setSensor] = useState("ALL");
    const [includeIncompleteCaptures, setIncludeIncompleteCaptures] = useState(false);
    const [itemIdType, setItemIdType] = useState('');
    const [itemId, setItemId] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showRegionError, setShowRegionError] = useState(false);
    const [showHardwareSourceError, setShowHardwareSourceError] = useState(false);
    const [showItemIdTypeError, setShowItemIdTypeError] = useState(false);
    const [showItemIdError, setShowItemIdError] = useState(false);

    useEffect(() => {
        if (initialInputData) {
            setRegion(initialInputData.region);
            setHardwareSource(initialInputData.hardwareSource);
            if (initialInputData.sensorType && initialInputData.sensorType !== 'ALL') {
                setSensor(initialInputData.sensorType);
            }
            if(initialInputData.includeIncompleteCaptures) {
                setIncludeIncompleteCaptures(initialInputData.includeIncompleteCaptures);
            }
            setItemIdType(initialInputData.itemIdType);
            setItemId(initialInputData.itemId);
        }
    },[initialInputData]);
   
    const handleRegionSelect = (value: string) => {
        setRegion(value);
        //when change region, reset hardware source and sensor value to default
        setHardwareSource('');
        setSensor('ALL');
        setShowRegionError(false);  
    }

    const handleHardwareSourceSelect = (value: string) => {
        setHardwareSource(value);
        setSensor('ALL');
        setIncludeIncompleteCaptures(false);
        setShowHardwareSourceError(false);
    }

    const handleSensorSelect = (value: string) => {
        setSensor(value);
    }

    const handleIncludeIncompleteCapturesCheck = (value: string) => {
        setIncludeIncompleteCaptures(value == 'true');
    }

    const handleItemIdTypeSelect = (value: string) => {
        setItemIdType(value);
        setShowItemIdTypeError(false);
    }

    const handleItemIdInput = (value: string) => {
        setItemId(value.trim());
        setShowItemIdError(false);
    }

    let relatedHardwareSource: { name: string; value: string; }[] =[];
    if (region !== "") {
        const regionTyped = region as keyof typeof REGION_HARDWARE_SOURCE_MAP;
        relatedHardwareSource = REGION_HARDWARE_SOURCE_MAP[regionTyped];
    }

    let relatedIncompleteCapturesOptions: { name: string; value: boolean; disabled: boolean}[] =[];
    if (hardwareSource !== "") {
        const hardwareSourceTyped = hardwareSource as keyof typeof HARDWARE_SOURCE_TO_INCOMPLETE_CAPTURE_MAP;
        relatedIncompleteCapturesOptions = HARDWARE_SOURCE_TO_INCOMPLETE_CAPTURE_MAP[hardwareSourceTyped];
    } else {
        relatedIncompleteCapturesOptions = HARDWARE_SOURCE_TO_INCOMPLETE_CAPTURE_MAP["ALL"];
    }

     //check item id validation
     const isInputItemIdValid = itemId ? inputValidation.isItemIdValid(itemId) : true;

    const onSubmitClick =() => { 
        //check if all required fields are provided
        if (region === "" || hardwareSource === "" || itemIdType === "" || itemId === "") {
            setErrorMessage("Region, Hardware Source, Item Identifier Type, and Item Identifier are required field!");
            
            if(region === "") {
                setShowRegionError(true);    
            }

            if(hardwareSource === "") {
               setShowHardwareSourceError(true);  
            }

            if(itemIdType === "") {
                setShowItemIdTypeError(true);
            }

            if(itemId === "") {
                setShowItemIdError(true);
            }
            return;
        }

        //check if input is valid
        if (!isInputItemIdValid) {
            setShowItemIdError(true);
            setErrorMessage("Item Identifier is not valid!");
            return;
        }

        let inputData = {
            itemId: itemId,
            itemIdType: itemIdType,
            hardwareSource: hardwareSource,
            sensorType: sensor,
            region: region,
            includeIncompleteCaptures: includeIncompleteCaptures
        } 

        onSubmit(inputData);
        setErrorMessage("");
    }

    let errorField =  (
        <Grid 
            className={styles.errorGrid}
            container 
            alignItems="center">
            <Typography 
                component='h4' 
                className={(errorMessage === "" ? "" : `${styles.error}`)}>
                {errorMessage}
            </Typography>
        </Grid>
    );

   return (
        <>
            <Container  paperProps={{ elevation: 2 }} boxProps={{ p: 1}}>
                <div className={styles.formGrid}>
                    <Grid item >
                        <FormControl fullWidth>
                            <FormSelect 
                                selectedValue={region}
                                label="Region:" 
                                placeholder="Please select region"
                                showError={showRegionError}
                                options={REGION_DROPDOWN} 
                                onSelect={handleRegionSelect}/>
                        </FormControl>
                    </Grid>
                    <HardwareSourceAndSensorGroupSelects
                        hardwareSource={hardwareSource}
                        sensorType={sensor}
                        hardwareSourceOptions={relatedHardwareSource}
                        showHardwareSourceError={showHardwareSourceError}
                        onSelectHardwareSource={handleHardwareSourceSelect }
                        onSelectSensor={handleSensorSelect} />
                    <Grid item >
                        <FormControl fullWidth>
                            <RadioButton
                                defaultValue={includeIncompleteCaptures}
                                label="Include Incomplete Captures:"
                                options={relatedIncompleteCapturesOptions}
                                onCheck={handleIncludeIncompleteCapturesCheck} />
                        </FormControl>
                    </Grid>
                    <Grid item >
                        <FormControl fullWidth>
                            <FormSelect 
                                selectedValue={itemIdType}
                                showError={showItemIdTypeError}
                                label="Item Identifier Type:" 
                                placeholder="Please select item Id Type"
                                options={ITEM_ID_TYPE_DROPDOWN} 
                                onSelect={handleItemIdTypeSelect}/>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl fullWidth>
                            <FormTextInput 
                                inputValue={itemId}
                                label="Item Identifier:" 
                                placeholder="Please input Item Id"
                                showError={showItemIdError}
                                onInput={handleItemIdInput}
                                showHelp={false}/>
                        </FormControl>
                    </Grid>
                </div>
                <Grid item >
                    <FormControl fullWidth>
                        <Button
                            className={styles.searchButton}
                            variant="contained"
                            onClick={onSubmitClick}>
                                Search
                        </Button>
                    </FormControl>
                </Grid>
            </Container>
            {errorField} 
        </>
    ); 
}

export default SearchForm;